// src/components/WhatsAppContactsPicker.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Dialog,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Paper,
  InputAdornment,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  FormControlLabel,
} from "@mui/material";
import {
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import api from "../axiosConfig";
import { getLeadStatusOptions, labelFor } from "../utils/leadStatusOptions";
import * as XLSX from "xlsx";

const WA_GREEN = "#1F8A5B";
const BORDER = "#E9ECEF";
const BG = "#F6F8FA";
const SOFT = "#EFF6FF";
const DEFAULT_LIMIT = 200;

const SOURCE_OPTIONS = [
  { value: "", label: "All Sources" },
  { value: "hubspot", label: "HubSpot" },
  { value: "inquiries", label: "Inquiries" },
  { value: "google", label: "Google" },
  { value: "excel", label: "Excel" },
  { value: "manual", label: "Manual" },
];

const ALLOWED_SOURCES_SET = new Set(["hubspot", "inquiries", "google", "excel", "manual"]);
const REMOTE_SYNCABLE_SOURCES = new Set(["", "hubspot", "inquiries", "google"]);

const CATEGORY_OPTIONS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
  { value: "staff", label: "Staff" },
];

const CONTACTED_OPTIONS = [
  { value: "", label: "All" },
  { value: "no", label: "Not contacted" },
  { value: "yes", label: "Contacted" },
];

const CONTACTED_LAST_DAYS_OPTIONS = [
  { value: "", label: "Any time" },
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function normalizeEmail(v) {
  const s = String(v || "").trim().toLowerCase();
  return s === "" ? "" : s;
}

function normalizePhone(v) {
  return String(v || "").trim();
}

function buildLaravelErrorMessage(e, fallback = "Request failed.") {
  const msg = e?.response?.data?.message;
  const errors = e?.response?.data?.errors;

  if (errors && typeof errors === "object") {
    const lines = [];
    for (const [field, arr] of Object.entries(errors)) {
      if (Array.isArray(arr) && arr.length) lines.push(`${field}: ${arr.join(" ")}`);
    }
    if (lines.length) return lines.join("\n");
  }

  return msg || e?.message || fallback;
}

/* ---------------- Excel/CSV parsing ---------------- */
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const uniqByPhoneOrEmail = (arr) => {
  const seen = new Set();
  const out = [];
  for (const c of arr || []) {
    const phone = normalizePhone(c?.primary_phone);
    const email = normalizeEmail(c?.primary_email);

    const k = phone ? `p:${phone}` : email ? `e:${email}` : "";
    if (!k || seen.has(k)) continue;

    seen.add(k);
    out.push(c);
  }
  return out;
};

const NORMALIZED_EMAIL_HEADERS = new Set([
  "email",
  "e-mail",
  "emailaddress",
  "primaryemail",
  "email_address",
  "mail",
]);
const NORMALIZED_NAME_HEADERS = new Set([
  "name",
  "fullname",
  "full_name",
  "contactname",
  "displayname",
]);
const NORMALIZED_FIRST_HEADERS = new Set(["first", "firstname", "givenname", "given_name"]);
const NORMALIZED_LAST_HEADERS = new Set(["last", "lastname", "familyname", "family_name", "surname"]);
const NORMALIZED_PHONE_HEADERS = new Set([
  "phone",
  "phonenumber",
  "phone_number",
  "mobile",
  "mobile_number",
  "cell",
  "cellphone",
  "telephone",
]);
const NORMALIZED_CATEGORY_HEADERS = new Set(["category", "segment", "type", "tag"]);

const normalizeKey = (s) =>
  String(s || "")
    .replace(/\s+/g, "")
    .replace(/[_-]/g, "")
    .toLowerCase();

function pickHeaderIndexes(rows2d) {
  for (let r = 0; r < Math.min(rows2d.length, 20); r++) {
    const row = rows2d[r] || [];
    const normed = row.map(normalizeKey);

    let emailIdx = -1,
      nameIdx = -1,
      firstIdx = -1,
      lastIdx = -1,
      phoneIdx = -1,
      categoryIdx = -1;

    normed.forEach((v, idx) => {
      if (emailIdx === -1 && NORMALIZED_EMAIL_HEADERS.has(v)) emailIdx = idx;
      if (nameIdx === -1 && NORMALIZED_NAME_HEADERS.has(v)) nameIdx = idx;
      if (firstIdx === -1 && NORMALIZED_FIRST_HEADERS.has(v)) firstIdx = idx;
      if (lastIdx === -1 && NORMALIZED_LAST_HEADERS.has(v)) lastIdx = idx;
      if (phoneIdx === -1 && NORMALIZED_PHONE_HEADERS.has(v)) phoneIdx = idx;
      if (categoryIdx === -1 && NORMALIZED_CATEGORY_HEADERS.has(v)) categoryIdx = idx;
    });

    if (emailIdx !== -1 || phoneIdx !== -1) {
      return { headerRow: r, emailIdx, nameIdx, firstIdx, lastIdx, phoneIdx, categoryIdx };
    }
  }
  return null;
}

function parseExcelOrCsv(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows2d = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: "",
          raw: false,
          blankrows: false,
        });

        if (!rows2d || rows2d.length === 0) return resolve([]);

        const headerInfo = pickHeaderIndexes(rows2d);
        if (!headerInfo) return resolve([]);

        const startRow = headerInfo.headerRow >= 0 ? headerInfo.headerRow + 1 : 0;
        const contacts = [];

        for (let r = startRow; r < rows2d.length; r++) {
          const row = rows2d[r] || [];

          let email =
            headerInfo.emailIdx !== -1 ? String(row[headerInfo.emailIdx] || "").trim() : "";
          if (email && /[,;]\s*/.test(email)) {
            email = email.split(/[,;]\s*/).find((e) => emailRegex.test(e)) || "";
          }
          email = normalizeEmail(email);

          let phone =
            headerInfo.phoneIdx !== -1 ? String(row[headerInfo.phoneIdx] || "").trim() : "";
          phone = normalizePhone(phone);

          if (!email && !phone) continue;
          if (email && !emailRegex.test(email)) email = "";

          let name = "";
          if (headerInfo.nameIdx !== -1) {
            name = String(row[headerInfo.nameIdx] || "").trim();
          } else {
            const first =
              headerInfo.firstIdx !== -1 ? String(row[headerInfo.firstIdx] || "").trim() : "";
            const last =
              headerInfo.lastIdx !== -1 ? String(row[headerInfo.lastIdx] || "").trim() : "";
            name = (first || last) ? `${first} ${last}`.trim() : "";
          }

          let category = "standard";
          if (headerInfo.categoryIdx !== -1) {
            const rawCat = String(row[headerInfo.categoryIdx] || "").trim().toLowerCase();
            if (rawCat === "vip" || rawCat === "staff" || rawCat === "standard") category = rawCat;
          }

          contacts.push({
            id: null,
            display_name: name || email || phone,
            primary_email: email || "",
            primary_phone: phone || "",
            category,
            _row: r,
            _tmp: `tmp_${r}_${Math.random().toString(16).slice(2)}`,
          });
        }

        resolve(uniqByPhoneOrEmail(contacts));
      } catch (e) {
        reject(e);
      }
    };

    reader.readAsArrayBuffer(file);
  });
}

function contactsToCsvFile(contacts, filename = "import.csv") {
  const header = ["display_name", "primary_phone", "primary_email", "category"];
  const escape = (v) => {
    const s = String(v ?? "");
    if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const lines = [
    header.join(","),
    ...contacts.map((c) =>
      [
        escape(c.display_name || ""),
        escape(c.primary_phone || ""),
        escape(c.primary_email || ""),
        escape(c.category || "standard"),
      ].join(",")
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  return new File([blob], filename, { type: "text/csv" });
}
/* ---------------- end Excel/CSV parsing ---------------- */

export default function WhatsAppContactsPicker({
  open = true,
  fullScreen = true,
  embedded = false,
  title = "Contacts",
  subtitle = "Select specific contacts (checkboxes). Only selected IDs will be targeted.",
  onClose,
  selectionModel,
  onSelectionChange,
  filterModel,
  onFilterChange,
  selectionEnabled = true,
  limit = DEFAULT_LIMIT,
  onContinue,
  continueLabel = "Continue",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingManual, setSavingManual] = useState(false);
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [syncStatus, setSyncStatus] = useState(null);

  const [statusOptions, setStatusOptions] = useState([]);
  const [localQ, setLocalQ] = useState(filterModel?.q || "");

  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: limit,
  });

  const [importLabel, setImportLabel] = useState("No file");
  const [importReady, setImportReady] = useState(false);
  const [importCount, setImportCount] = useState(0);

  const [openManual, setOpenManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    display_name: "",
    primary_phone: "",
    primary_email: "",
    category: "standard",
  });
  const [manualErr, setManualErr] = useState("");

  const [openCreateDb, setOpenCreateDb] = useState(false);
  const [dbName, setDbName] = useState("");
  const [dbDesc, setDbDesc] = useState("");
  const [creatingDb, setCreatingDb] = useState(false);
  const [dbErr, setDbErr] = useState("");

  const [startWithSelected, setStartWithSelected] = useState(false);
  const [dbSelectedSnapshot, setDbSelectedSnapshot] = useState([]);
  const [dbSelectedCountSnapshot, setDbSelectedCountSnapshot] = useState(0);

  const [dbContacts, setDbContacts] = useState([]);
  const [dbContactsBusy, setDbContactsBusy] = useState(false);

  const [dbManual, setDbManual] = useState({
    display_name: "",
    primary_phone: "",
    primary_email: "",
  });
  const [dbManualErr, setDbManualErr] = useState("");

  const [dbEditing, setDbEditing] = useState(null);
  const [contactGroups, setContactGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const CONTACT_GROUPS_ENDPOINT = "/marketing/contact-groups";
  const CONTACTS_ENDPOINT = "/marketing/contacts";
  const requestSeq = useRef(0);
  const syncPollTokenRef = useRef(0);
  const isMountedRef = useRef(true);
  const onFilterChangeRef = useRef(onFilterChange);

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      syncPollTokenRef.current += 1;
    };
  }, []);

  const filterSource = String(filterModel?.source ?? "").trim();
  const filterLeadStatus = String(filterModel?.lead_status ?? filterModel?.status ?? "").trim();
  const filterQuery = String(filterModel?.q ?? "").trim();
  const filterContacted = String(filterModel?.contacted ?? "").trim().toLowerCase();
  const filterContactedLastDays = String(filterModel?.contacted_last_days ?? "").trim();
  const filterExcludeCampaignId = String(filterModel?.exclude_campaign_id ?? "").trim();
  const filterCampaignId = String(filterModel?.campaign_id ?? "").trim();
  const filterContactGroupId = String(filterModel?.contact_group_id ?? "").trim();
  const filterPreferredLimit = String(filterModel?.preferred_limit ?? "").trim();
  const filterLimit = String(filterModel?.limit ?? "").trim();

  const safeFilterModel = useMemo(
    () => ({
      source: filterSource === "all" ? "" : filterSource,
      lead_status: filterLeadStatus,
      status: filterLeadStatus,
      q: filterQuery,
      contacted: ["yes", "no"].includes(filterContacted) ? filterContacted : "",
      contacted_last_days: ["7", "14", "30"].includes(filterContactedLastDays) ? filterContactedLastDays : "",
      exclude_campaign_id: /^\d+$/.test(filterExcludeCampaignId) ? filterExcludeCampaignId : "",
      campaign_id: /^\d+$/.test(filterCampaignId) ? filterCampaignId : "",
      contact_group_id: /^\d+$/.test(filterContactGroupId) ? filterContactGroupId : "",
      preferred_limit: filterPreferredLimit,
      limit: filterLimit,
    }),
    [
      filterCampaignId,
      filterContactGroupId,
      filterContacted,
      filterContactedLastDays,
      filterExcludeCampaignId,
      filterLeadStatus,
      filterLimit,
      filterPreferredLimit,
      filterQuery,
      filterSource,
    ]
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const opts = await getLeadStatusOptions();
        if (mounted) setStatusOptions(Array.isArray(opts) ? opts : []);
      } catch (e) {
        console.error("Failed to load lead status options", e);
        if (mounted) setStatusOptions([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setGroupsLoading(true);
      try {
        const res = await api.get(CONTACT_GROUPS_ENDPOINT);
        const list = res?.data?.data ?? res?.data ?? [];
        if (!mounted) return;
        setContactGroups(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setContactGroups([]);
      } finally {
        if (mounted) setGroupsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setLocalQ(safeFilterModel?.q || "");
  }, [safeFilterModel?.q]);

  // performance fix: do not depend on safeFilterModel object
  useEffect(() => {
    if (openCreateDb) return;
    const t = setTimeout(() => {
      if (localQ !== (safeFilterModel?.q || "")) {
        onFilterChangeRef.current?.({ ...safeFilterModel, q: localQ });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [localQ, openCreateDb, safeFilterModel]);

  const leadStatusValue = safeFilterModel.lead_status || "";

  useEffect(() => {
    if (openCreateDb) return;
    setPage(1);
  }, [
    safeFilterModel.source,
    safeFilterModel.contact_group_id,
    leadStatusValue,
    safeFilterModel.q,
    safeFilterModel.contacted,
    safeFilterModel.contacted_last_days,
    safeFilterModel.exclude_campaign_id,
    safeFilterModel.campaign_id,
    limit,
    openCreateDb,
  ]);

  const buildParams = useCallback(
    (overrideModel, overridePage) => {
      const params = new URLSearchParams();
      const perPage = Math.max(1, Math.min(Number(limit || DEFAULT_LIMIT), 2000));

      params.set("limit", String(perPage));
      params.set("page", String(overridePage ?? page));

      const model = overrideModel ?? safeFilterModel ?? {};
      const src = String(model?.source ?? "").trim();
      const lead_status = String(model?.lead_status ?? model?.status ?? "").trim();
      const q = String(model?.q ?? "").trim();
      const contact_group_id = String(model?.contact_group_id ?? "").trim();

      if (src) params.set("source", src);
      if (lead_status) params.set("lead_status", lead_status);
      if (q) params.set("q", q);

      const contacted = String(model?.contacted ?? "").trim().toLowerCase();
      if (contacted === "yes" || contacted === "no") params.set("contacted", contacted);

      const days = String(model?.contacted_last_days ?? "").trim();
      if (["7", "14", "30"].includes(days)) params.set("contacted_last_days", days);

      const excludeCampaignId = String(model?.exclude_campaign_id ?? "").trim();
      if (/^\d+$/.test(excludeCampaignId)) params.set("exclude_campaign_id", excludeCampaignId);

      const campaignId = String(model?.campaign_id ?? "").trim();
      if (/^\d+$/.test(campaignId)) params.set("campaign_id", campaignId);

      // database filter: backend should filter contacts by selected database/group
      if (/^\d+$/.test(contact_group_id)) params.set("contact_group_id", contact_group_id);

      return params;
    },
    [safeFilterModel, limit, page]
  );

  const fetchContacts = useCallback(
    async (overrideModel, overridePage) => {
      setLoading(true);
      setErr("");
      setSuccessMsg("");

      const seq = ++requestSeq.current;

      try {
        const params = buildParams(overrideModel, overridePage);
        const res = await api.get(`/marketing/contacts?${params.toString()}`);
        const payload = res?.data ?? {};

        if (seq !== requestSeq.current) return;

        const list = payload?.data ?? [];
        setRows(Array.isArray(list) ? list : []);
        setPageMeta({
          current_page: Number(payload?.current_page ?? (overridePage ?? page)),
          last_page: Number(payload?.last_page ?? 1),
          total: Number(payload?.total ?? (Array.isArray(list) ? list.length : 0)),
          per_page: Number(payload?.per_page ?? Math.max(1, Math.min(Number(limit || DEFAULT_LIMIT), 2000))),
        });
      } catch (e) {
        console.error(e);
        if (seq !== requestSeq.current) return;

        setRows([]);
        setPageMeta({
          current_page: 1,
          last_page: 1,
          total: 0,
          per_page: Math.max(1, Math.min(Number(limit || DEFAULT_LIMIT), 2000)),
        });
        setErr(buildLaravelErrorMessage(e, "Failed to load contacts."));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [buildParams, page, limit]
  );

  useEffect(() => {
    if (!open) return;
    if (openCreateDb) return;
    fetchContacts(undefined, page);
  }, [
    open,
    page,
    safeFilterModel.source,
    safeFilterModel.contact_group_id,
    leadStatusValue,
    safeFilterModel.q,
    safeFilterModel.contacted,
    safeFilterModel.contacted_last_days,
    safeFilterModel.exclude_campaign_id,
    safeFilterModel.campaign_id,
    limit,
    fetchContacts,
    openCreateDb,
  ]);

  const currentSource = String(safeFilterModel?.source ?? "");
  const currentSourceLabel =
    SOURCE_OPTIONS.find((s) => s.value === currentSource)?.label || "All Sources";
  const supportsRemoteSync = REMOTE_SYNCABLE_SOURCES.has(currentSource);

  const syncSource = currentSource === "" ? "all" : currentSource;
  const syncLabel = supportsRemoteSync
    ? (currentSource === "" ? "Sync All Sources" : `Sync ${currentSourceLabel}`)
    : `Refresh ${currentSourceLabel}`;
  const syncHelperText = supportsRemoteSync
    ? `${syncLabel} - pull latest contacts from your integrations`
    : `${syncLabel} - reload contacts already saved in the contact database`;
  const syncStatusMeta = useMemo(() => {
    const status = String(syncStatus?.status || "").toLowerCase();
    if (!status) return null;

    if (status === "done") return { label: "Sync complete", color: "success" };
    if (status === "failed") return { label: "Sync failed", color: "error" };
    if (status === "running") return { label: "Sync running", color: "info" };
    if (status === "queued") return { label: "Sync queued", color: "info" };

    return { label: `Sync ${status}`, color: "default" };
  }, [syncStatus]);

  const pollSyncStatus = useCallback(
    async (jobId) => {
      const pollToken = ++syncPollTokenRef.current;
      const deadline = Date.now() + 2 * 60 * 1000;

      while (isMountedRef.current && syncPollTokenRef.current === pollToken) {
        const res = await api.get(`/marketing/sync/status/${jobId}`);
        const payload = res?.data ?? {};

        if (!isMountedRef.current || syncPollTokenRef.current !== pollToken) {
          return null;
        }

        setSyncStatus(payload);

        const status = String(payload?.status || "").toLowerCase();
        if (status === "done" || status === "failed") {
          return payload;
        }

        if (Date.now() >= deadline) {
          const timeoutPayload = {
            status: "failed",
            source: payload?.source || syncSource,
            message: "Sync status polling timed out. Please refresh contacts in a moment.",
          };
          setSyncStatus(timeoutPayload);
          return timeoutPayload;
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      return null;
    },
    [syncSource]
  );

  const handleSync = async () => {
    setSyncing(true);
    setErr("");
    setSuccessMsg("");
    try {
      if (!supportsRemoteSync) {
        setPage(1);
        await fetchContacts(undefined, 1);
        setSyncStatus({
          status: "done",
          source: currentSource || "all",
          message: "Contacts refreshed from the contact database.",
        });
        setSuccessMsg("Contacts refreshed from the contact database.");
        return;
      }

      const res = await api.post("/marketing/sync", { source: syncSource });
      const jobId = String(res?.data?.sync_job_id || "").trim();

      if (!jobId) {
        const waitMs = ((res?.data?.wait_seconds) || 10) * 1000;
        setSyncStatus({
          status: "running",
          source: res?.data?.source || syncSource,
          message: `Sync queued - refreshing contacts in ${Math.round(waitMs / 1000)}s...`,
        });
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        setPage(1);
        await fetchContacts(undefined, 1);
        setSyncStatus({
          status: "done",
          source: res?.data?.source || syncSource,
          message: "Sync complete. Contacts refreshed.",
        });
        setSuccessMsg("Sync complete. Contacts refreshed.");
        return;
      }

      setSyncStatus({
        status: String(res?.data?.status || "queued").toLowerCase(),
        source: res?.data?.source || syncSource,
        started_at: null,
        done_at: null,
        message: res?.data?.message || `Sync queued for ${currentSourceLabel}.`,
      });

      const finalStatus = await pollSyncStatus(jobId);
      if (String(finalStatus?.status || "").toLowerCase() === "done") {
        setPage(1);
        await fetchContacts(undefined, 1);
        setSuccessMsg("Sync complete. Contacts refreshed.");
        return;
      }

      throw new Error(finalStatus?.message || "Failed to sync contacts.");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || e?.message || `Failed to sync contacts.`);
    } finally {
      setSyncing(false);
    }
  };

  const handlePickFile = useCallback(async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;

    setImportLabel(`${file.name} - parsing...`);
    setImportReady(false);
    setImportCount(0);
    setErr("");
    setSuccessMsg("");

    try {
      const contacts = await parseExcelOrCsv(file);
      const withPhone = (contacts || []).filter((c) => normalizePhone(c?.primary_phone));

      if (!withPhone.length) {
        setImportLabel(`${file.name} - not ready`);
        setImportReady(false);
        setErr("WhatsApp imports require phone numbers. Please use a file with a phone column.");
        return;
      }

      const csvFile = contactsToCsvFile(
        withPhone,
        file.name.replace(/\.(xlsx|xls|csv)$/i, "") + ".csv"
      );

      setUploading(true);

      const form = new FormData();
      form.append("file", csvFile);

      await api.post("/marketing/contacts/import-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImportCount(withPhone.length);
      setImportReady(true);
      setImportLabel(`${file.name} - ready (${withPhone.length} phone-safe)`);

      const nextModel = { ...(safeFilterModel || {}), source: "excel" };
      onFilterChange?.(nextModel);

      setPage(1);
      await fetchContacts(nextModel, 1);
      setSuccessMsg(`Imported ${withPhone.length} phone-safe contacts.`);
    } catch (e) {
      console.error(e);
      setImportReady(false);
      setImportLabel("Failed parsing");
      setErr(buildLaravelErrorMessage(e, "Failed to parse or import file."));
    } finally {
      setUploading(false);
      if (evt?.target) evt.target.value = "";
    }
  }, [fetchContacts, onFilterChange, safeFilterModel]);

  const openManualModal = () => {
    setManualErr("");
    setManualForm({
      display_name: "",
      primary_phone: "",
      primary_email: "",
      category: "standard",
    });
    setOpenManual(true);
  };

  const normId = useCallback((v) => String(v), []);

  const selectedSet = useMemo(
    () => new Set(Array.from(selectionModel || []).map(normId)),
    [selectionModel, normId]
  );

  const visibleIds = useMemo(() => (rows || []).map((r) => normId(r.id)), [rows, normId]);

  const allVisibleSelected =
    selectionEnabled && visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));

  const someVisibleSelected = selectionEnabled && visibleIds.some((id) => selectedSet.has(id));

  const toggle = useCallback(
    (idRaw) => {
      if (!selectionEnabled) return;
      const id = normId(idRaw);
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange?.(next);
    },
    [selectionEnabled, normId, selectedSet, onSelectionChange]
  );

  const toggleAllVisible = useCallback(() => {
    if (!selectionEnabled) return;
    const next = new Set(selectedSet);
    const all = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
    if (all) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    onSelectionChange?.(next);
  }, [selectionEnabled, selectedSet, visibleIds, onSelectionChange]);

  const selectedCount = selectionEnabled ? selectedSet.size : 0;

  const saveManual = async () => {
    setManualErr("");

    const display_name = String(manualForm.display_name || "").trim();
    const primary_phone = normalizePhone(manualForm.primary_phone);
    const primary_email = normalizeEmail(manualForm.primary_email);
    const category = String(manualForm.category || "standard").toLowerCase();

    if (!primary_phone) {
      setManualErr("Phone number is required.");
      return;
    }

    const contactPayload = {
      display_name,
      primary_phone,
      primary_email,
      category,
      name: display_name,
      phone: primary_phone,
      email: primary_email,
    };

    setSavingManual(true);
    try {
      const res = await api.post(CONTACTS_ENDPOINT, { contacts: [contactPayload] });

      const created =
        (Array.isArray(res?.data?.contacts) && res.data.contacts[0]) ||
        res?.data?.contact ||
        (Array.isArray(res?.data?.data) && res.data.data[0]) ||
        res?.data?.data ||
        null;

      let createdId = created?.id != null ? String(created.id) : "";

      const nextModel = { ...(safeFilterModel || {}), source: "manual" };
      onFilterChange?.(nextModel);

      setPage(1);
      await fetchContacts(nextModel, 1);

      if (!createdId) {
        const q = primary_email || primary_phone;
        if (q) {
          const params = new URLSearchParams();
          params.set("limit", "50");
          params.set("page", "1");
          params.set("source", "manual");
          params.set("q", q);

          const lookup = await api.get(`/marketing/contacts?${params.toString()}`);
          const found = (lookup?.data?.data || [])[0];
          if (found?.id != null) createdId = String(found.id);
        }
      }

      setOpenManual(false);
      setSuccessMsg("Contact saved.");

      if (selectionEnabled && createdId) {
        const nextSel = new Set(selectedSet);
        nextSel.add(createdId);
        onSelectionChange?.(nextSel);
      }
    } catch (e) {
      console.error(e);
      setManualErr(buildLaravelErrorMessage(e, "Failed to save contact."));
    } finally {
      setSavingManual(false);
    }
  };

  const canContinue =
    !embedded && selectionEnabled && selectedCount > 0 && typeof onContinue === "function";

  const handleContinue = () => {
    if (typeof onContinue !== "function") {
      setErr("Picker is missing onContinue handler (parent must pass it).");
      return;
    }

    const ids = Array.from(selectedSet);

    onContinue({
      recipient_mode: "selected",
      selected_ids: ids,
      filter_model: { ...(safeFilterModel || {}) },
    });

    onClose?.();
  };

  const infoText = selectionEnabled
    ? `Info: Select exact contacts using checkboxes. Only selected IDs will be targeted. Current source filter: ${currentSourceLabel} (browsing only).`
    : `Info: Preview matching contacts. Selection is disabled in this mode. Current source filter: ${currentSourceLabel}.`;

  const handleSourceChange = (newSourceRaw) => {
    const newSource = String(newSourceRaw ?? "").trim();
    const normalized = newSource === "all" ? "" : newSource;

    if (normalized !== "" && !ALLOWED_SOURCES_SET.has(normalized)) {
      setErr("Invalid source selected.");
      return;
    }

    onFilterChange?.({ ...(safeFilterModel || {}), source: normalized });
  };

  const handleLeadStatusChange = (newLead) => {
    const v = String(newLead ?? "").trim();
    onFilterChange?.({ ...(safeFilterModel || {}), lead_status: v, status: v });
  };

  // database filter
  const handleContactGroupChange = (vRaw) => {
    const s = String(vRaw ?? "").trim();
    const val = /^\d+$/.test(s) ? s : "";
    onFilterChange?.({ ...(safeFilterModel || {}), contact_group_id: val });
  };

  const handleContactedChange = (vRaw) => {
    const v = String(vRaw ?? "").trim().toLowerCase();
    const contacted = v === "yes" || v === "no" ? v : "";
    const next = { ...(safeFilterModel || {}), contacted };
    if (!contacted) next.contacted_last_days = "";
    onFilterChange?.(next);
  };

  const handleContactedDaysChange = (vRaw) => {
    const v = String(vRaw ?? "").trim();
    const days = ["7", "14", "30"].includes(v) ? v : "";
    const next = { ...(safeFilterModel || {}), contacted_last_days: days };
    if (days) next.contacted = "yes";
    onFilterChange?.(next);
  };

  const handleExcludeCampaignChange = (vRaw) => {
    const s = String(vRaw ?? "").trim();
    const val = /^\d+$/.test(s) ? s : "";
    onFilterChange?.({ ...(safeFilterModel || {}), exclude_campaign_id: val });
  };

  const openCreateDbModal = (opts = { preselect: false }) => {
    setDbErr("");
    setSuccessMsg("");
    setDbName("");
    setDbDesc("");
    setDbContacts([]);
    setDbContactsBusy(false);
    setDbManualErr("");
    setDbManual({ display_name: "", primary_phone: "", primary_email: "" });

    const wantPreselect = Boolean(opts?.preselect);
    const canPreselect = wantPreselect && selectionEnabled && selectedCount > 0;

    setStartWithSelected(canPreselect);

    const snap = canPreselect ? Array.from(selectedSet) : [];
    setDbSelectedSnapshot(snap);
    setDbSelectedCountSnapshot(canPreselect ? snap.length : 0);

    setOpenCreateDb(true);
  };

  // FIX: support contact_ids returned by backend
  const addDbManualContact = async () => {
    setDbManualErr("");
    setDbErr("");

    const display_name = String(dbManual.display_name || "").trim();
    const primary_phone = normalizePhone(dbManual.primary_phone);
    const primary_email = normalizeEmail(dbManual.primary_email);

    if (!primary_phone) {
      setDbManualErr("Phone number is required.");
      return;
    }

    const payload = {
      contacts: [
        {
          display_name: display_name || primary_phone,
          primary_phone,
          primary_email: primary_email || "",
          category: "standard",
          name: display_name || primary_phone,
          phone: primary_phone,
          email: primary_email || "",
        },
      ],
    };

    setDbContactsBusy(true);
    try {
      const res = await api.post(CONTACTS_ENDPOINT, payload);

      const createdId = res?.data?.contact_ids?.[0] || res?.data?.id;

      if (!createdId) {
        throw new Error("Contact created but no id returned from API.");
      }

      const newRow = {
        id: String(createdId),
        display_name: display_name || primary_phone,
        primary_phone,
        primary_email: primary_email || "",
        category: "standard",
      };

      setDbContacts((prev) => uniqByPhoneOrEmail([newRow, ...(prev || [])]));
      setDbManual({ display_name: "", primary_phone: "", primary_email: "" });
    } catch (e) {
      console.error(e);
      setDbManualErr(buildLaravelErrorMessage(e, "Failed to create contact."));
    } finally {
      setDbContactsBusy(false);
    }
  };

  const handleDbImportFile = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;

    setDbErr("");
    setSuccessMsg("");
    setDbContactsBusy(true);

    try {
      const parsed = await parseExcelOrCsv(file);
      const withPhone = (parsed || []).filter((c) => normalizePhone(c?.primary_phone));

      if (!withPhone.length) {
        setDbErr("WhatsApp databases require phone numbers.");
        return;
      }

      setDbContacts((prev) => uniqByPhoneOrEmail([...(withPhone || []), ...(prev || [])]));
      setSuccessMsg(`Imported ${withPhone.length} contacts into this database builder.`);
    } catch (e) {
      console.error(e);
      setDbErr(buildLaravelErrorMessage(e, "Failed to import file into database builder."));
    } finally {
      setDbContactsBusy(false);
      if (evt?.target) evt.target.value = "";
    }
  };

  const openEditDbContact = useCallback((idx) => {
    const c = dbContacts[idx];
    if (!c) return;
    setDbEditing({
      idx,
      contact: {
        display_name: c.display_name || "",
        primary_phone: c.primary_phone || "",
        primary_email: c.primary_email || "",
      },
    });
  }, [dbContacts]);

  const saveEditDbContact = async () => {
    if (!dbEditing) return;

    setDbErr("");

    const display_name = String(dbEditing.contact.display_name || "").trim();
    const primary_phone = normalizePhone(dbEditing.contact.primary_phone);
    const primary_email = normalizeEmail(dbEditing.contact.primary_email);

    if (!primary_phone) {
      setDbErr("Phone number is required.");
      return;
    }

    const current = dbContacts[dbEditing.idx];
    if (!current) return;

    setDbContactsBusy(true);
    try {
      if (current.id) {
        await api.patch(`${CONTACTS_ENDPOINT}/${current.id}`, {
          display_name: display_name || primary_phone,
          primary_phone,
          primary_email: primary_email || "",
        });
      }

      setDbContacts((prev) => {
        const next = [...(prev || [])];
        const cur = next[dbEditing.idx];
        if (!cur) return prev;

        next[dbEditing.idx] = {
          ...cur,
          display_name: display_name || primary_phone,
          primary_phone,
          primary_email: primary_email || "",
        };

        return uniqByPhoneOrEmail(next);
      });

      setDbEditing(null);
    } catch (e) {
      console.error(e);
      setDbErr(buildLaravelErrorMessage(e, "Failed to update contact."));
    } finally {
      setDbContactsBusy(false);
    }
  };

  const removeDbContactLocal = useCallback((idx) => {
    setDbContacts((prev) => (prev || []).filter((_, i) => i !== idx));
  }, []);

  const ensureDbContactsHaveIds = async () => {
    const existingIds = (dbContacts || [])
      .map((c) => Number(c.id))
      .filter((n) => Number.isFinite(n));

    const toCreate = (dbContacts || []).filter((c) => !c.id);
    const toCreateWithPhone = toCreate.filter((c) => normalizePhone(c?.primary_phone));

    if (!toCreateWithPhone.length) return existingIds;

    const payload = {
      contacts: toCreateWithPhone.map((c) => {
        const dn = String(c.display_name || "").trim();
        const ph = normalizePhone(c.primary_phone);
        const em = normalizeEmail(c.primary_email);

        return {
          display_name: dn || ph,
          primary_phone: ph,
          primary_email: em || "",
          category: c.category || "standard",
          name: dn || ph,
          phone: ph,
          email: em || "",
        };
      }),
    };

    const res = await api.post(CONTACTS_ENDPOINT, payload);

    const idsFromApi = (Array.isArray(res?.data?.contact_ids) && res.data.contact_ids) || [];
    const createdIdsFromApi = idsFromApi.map((x) => Number(x)).filter((n) => Number.isFinite(n));

    const createdFallback =
      res?.data?.contacts || res?.data?.data || (Array.isArray(res?.data) ? res.data : []) || [];
    const createdIdsFallback = (Array.isArray(createdFallback) ? createdFallback : [])
      .map((x) => Number(x?.id))
      .filter((n) => Number.isFinite(n));

    const createdIds = createdIdsFromApi.length ? createdIdsFromApi : createdIdsFallback;
    return [...existingIds, ...createdIds];
  };

  const createDatabase = async () => {
    setDbErr("");
    setSuccessMsg("");

    const name = String(dbName || "").trim();
    const description = String(dbDesc || "").trim();

    if (name.length < 3) {
      setDbErr("Database name must be at least 3 characters.");
      return;
    }

    setCreatingDb(true);
    try {
      let selectedIds = [];
      if (startWithSelected && dbSelectedSnapshot.length > 0) {
        selectedIds = dbSelectedSnapshot.map((x) => Number(x)).filter((n) => Number.isFinite(n));
      }

      const modalIds = await ensureDbContactsHaveIds();
      const finalIds = Array.from(new Set([...(selectedIds || []), ...(modalIds || [])]));

      await api.post(CONTACT_GROUPS_ENDPOINT, {
        name,
        description: description || null,
        contact_ids: finalIds,
      });

      setOpenCreateDb(false);

      setSuccessMsg(
        finalIds.length
          ? `Database "${name}" created (${finalIds.length} contacts).`
          : `Database "${name}" created (empty).`
      );

      setPage(1);
      await fetchContacts(undefined, 1);
    } catch (e) {
      console.error(e);
      setDbErr(buildLaravelErrorMessage(e, "Failed to create database."));
    } finally {
      setCreatingDb(false);
    }
  };

  const filterControlSx = {
    minWidth: 0,
    width: "100%",
    "& .MuiInputBase-root": {
      height: 44,
      alignItems: "center",
      backgroundColor: "white",
    },
    "& .MuiFormHelperText-root": {
      display: "none",
    },
  };

  const tableBody = useMemo(() => {
    if (loading) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={selectionEnabled ? 7 : 6} align="center" sx={{ py: 6 }}>
              <CircularProgress />
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    if (rows.length === 0) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={selectionEnabled ? 7 : 6} align="center" sx={{ py: 6 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#111827" }}>
                No contacts found for this filter.
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Try adjusting your filters or syncing.
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Tip: you can import Excel/CSV even if this list is empty.
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={uploading}
                  sx={{
                    bgcolor: WA_GREEN,
                    fontWeight: 900,
                    textTransform: "none",
                    "&:hover": { bgcolor: WA_GREEN },
                  }}
                >
                  Import File
                  <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handlePickFile} />
                </Button>

                {importReady && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, color: "text.secondary" }}
                  >
                    Imported {importCount} rows successfully.
                  </Typography>
                )}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return (
      <TableBody>
        {rows.map((c) => {
          const id = normId(c.id);
          const isSelected = selectionEnabled && selectedSet.has(id);

          return (
            <TableRow
              key={id}
              hover
              selected={Boolean(isSelected)}
              onClick={() => !openCreateDb && toggle(id)}
              sx={{
                cursor: selectionEnabled && !openCreateDb ? "pointer" : "default",
                "&.Mui-selected": { bgcolor: "#ECFDF5" },
                "&.Mui-selected:hover": { bgcolor: "#D1FAE5" },
              }}
            >
              {selectionEnabled && (
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={Boolean(isSelected)}
                    onChange={() => toggle(id)}
                    disabled={openCreateDb}
                  />
                </TableCell>
              )}

              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {c.display_name || c.name || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  #{id}
                  {c.company ? ` • ${c.company}` : ""}
                  {c.primary_source ? ` • primary: ${c.primary_source}` : ""}
                </Typography>
              </TableCell>

              <TableCell>{c.primary_phone || c.phone_number || "—"}</TableCell>
              <TableCell>{c.primary_email || c.email || "—"}</TableCell>

              <TableCell>
                <Chip
                  size="small"
                  label={labelFor(statusOptions, c.lead_status) || "—"}
                  sx={{
                    bgcolor: "#F3F4F6",
                    fontWeight: 800,
                    border: `1px solid ${BORDER}`,
                  }}
                />
              </TableCell>

              <TableCell>
                {(c.sources || []).length ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(c.sources || []).map((s) => (
                      <Chip
                        key={`${s.source}-${s.external_id || ""}`}
                        size="small"
                        label={s.source}
                        variant="outlined"
                        sx={{ fontWeight: 800 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    —
                  </Typography>
                )}
              </TableCell>

              <TableCell align="right">
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {fmtDate(c.last_contacted_at)}
                </Typography>
                {typeof c.contact_interaction_count !== "undefined" && (
                  <Typography variant="caption" sx={{ display: "block", color: "#6B7280" }}>
                    Interactions: {Number(c.contact_interaction_count || 0)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  }, [
    loading,
    rows,
    selectionEnabled,
    selectedSet,
    openCreateDb,
    toggle,
    statusOptions,
    uploading,
    importReady,
    importCount,
    handlePickFile,
    normId,
  ]);

  const dbContactsRows = useMemo(() => {
    return (dbContacts || []).map((c, idx) => (
      <TableRow key={c.id ?? c._tmp ?? idx}>
        <TableCell>{c.display_name || "—"}</TableCell>
        <TableCell>{c.primary_phone || "—"}</TableCell>
        <TableCell>{c.primary_email || "—"}</TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="outlined"
              onClick={() => openEditDbContact(idx)}
              sx={{ fontWeight: 900, textTransform: "none" }}
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => removeDbContactLocal(idx)}
              sx={{ fontWeight: 900, textTransform: "none" }}
            >
              Remove
            </Button>
          </Stack>
        </TableCell>
      </TableRow>
    ));
  }, [dbContacts, openEditDbContact, removeDbContactLocal]);

  const content = (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        mx: 0,
        height: embedded ? "auto" : "100vh",
        bgcolor: embedded ? "transparent" : BG,
        p: embedded ? 0 : { xs: 1.5, md: 2.5 },
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
      }}
    >
      {!embedded && (
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: "white", borderBottom: `1px solid ${BORDER}` }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                {subtitle}
              </Typography>
            </Box>

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={() => fetchContacts(undefined, page)}
                  disabled={loading || syncing || uploading || openCreateDb}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title={
                syncing
                  ? "Syncing contacts, please wait..."
                  : syncHelperText
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSync}
                  disabled={syncing || loading || uploading || openCreateDb}
                  startIcon={
                    syncing ? (
                      <CircularProgress size={16} />
                    ) : supportsRemoteSync ? (
                      <SyncIcon />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  sx={{
                    bgcolor: WA_GREEN,
                    fontWeight: 900,
                    textTransform: "none",
                    "&:hover": { bgcolor: WA_GREEN },
                  }}
                >
                  {syncing ? "Syncing..." : syncLabel}
                </Button>
              </span>
            </Tooltip>

            {onClose && (
              <Tooltip title="Close">
                <IconButton onClick={onClose} size="small" sx={{ ml: 0.5 }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Paper
        elevation={0}
        sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, bgcolor: SOFT, p: 1.5 }}
      >
        <Typography variant="body2" sx={{ color: "#1F2937" }}>
          <b>Info:</b> {infoText.replace(/^Info:\s?/, "")}
        </Typography>
      </Paper>

      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg("")} sx={{ whiteSpace: "pre-wrap" }}>
          {successMsg}
        </Alert>
      )}

      {err && (
        <Alert severity="error" onClose={() => setErr("")} sx={{ whiteSpace: "pre-wrap" }}>
          {err}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 4,
          overflow: "hidden",
          bgcolor: "white",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${BORDER}`,
            background: "linear-gradient(135deg, rgba(31,138,91,0.08) 0%, rgba(255,255,255,0.96) 58%, rgba(241,245,249,0.92) 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Contacts
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {selectionEnabled
                  ? "Select specific contacts (checkboxes)."
                  : "Preview matching contacts (selection disabled)."}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                <Chip
                  label={`Source: ${currentSourceLabel}`}
                  variant="outlined"
                  sx={{ fontWeight: 900, borderColor: BORDER, bgcolor: "white" }}
                />
                <Chip
                  label={`Visible: ${Number(pageMeta.total || 0)}`}
                  variant="outlined"
                  sx={{ fontWeight: 900, borderColor: BORDER, bgcolor: "white" }}
                />
                <Chip
                  label={`Selected: ${selectedCount}`}
                  color={selectedCount ? "success" : "default"}
                  sx={{ fontWeight: 900 }}
                />
                <Chip
                  label={importReady ? `Import ready: ${importCount}` : "Import idle"}
                  variant="outlined"
                  sx={{ fontWeight: 900, borderColor: BORDER, bgcolor: "white" }}
                />
                <Chip
                  label={syncLabel}
                  variant="outlined"
                  sx={{ fontWeight: 900, borderColor: BORDER, bgcolor: "white" }}
                />
                {syncStatusMeta ? (
                  <Chip label={syncStatusMeta.label} color={syncStatusMeta.color} sx={{ fontWeight: 900 }} />
                ) : null}
              </Stack>
              {syncStatus?.message ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1,
                    display: "block",
                    color:
                      String(syncStatus?.status || "").toLowerCase() === "failed"
                        ? "error.main"
                        : "text.secondary",
                  }}
                >
                  {syncStatus.message}
                </Typography>
              ) : null}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
              useFlexGap
            >
              {embedded && (
                <Tooltip
                  title={
                    syncing
                      ? "Syncing contacts, please wait..."
                      : syncHelperText
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSync}
                      disabled={syncing || loading || uploading || openCreateDb}
                      startIcon={
                        syncing ? (
                          <CircularProgress size={16} />
                        ) : supportsRemoteSync ? (
                          <SyncIcon />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                      sx={{
                        bgcolor: WA_GREEN,
                        fontWeight: 900,
                        textTransform: "none",
                        height: 44,
                        "&:hover": { bgcolor: WA_GREEN },
                      }}
                    >
                      {syncing ? "Syncing..." : syncLabel}
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Tooltip title="Add a manual contact">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openManualModal}
                    disabled={loading || syncing || uploading || openCreateDb}
                    sx={{ textTransform: "none", fontWeight: 900, height: 44 }}
                  >
                    Add Contact
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Import Excel/CSV into Excel source">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={uploading || openCreateDb}
                    sx={{ textTransform: "none", fontWeight: 900, height: 44 }}
                  >
                    Import File
                    <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handlePickFile} />
                  </Button>

                  <Chip
                    label={importReady ? `${importLabel}` : uploading ? "Uploading…" : importLabel}
                    color={importReady ? "success" : "default"}
                    variant="outlined"
                    sx={{ fontWeight: 900, borderColor: BORDER, maxWidth: 320, height: 44 }}
                  />
                </span>
              </Tooltip>

              <Tooltip title="Create a reusable database/group">
                <span>
                  <Button
                    variant="contained"
                    onClick={() => openCreateDbModal({ preselect: false })}
                    disabled={loading || syncing || uploading}
                    sx={{
                      bgcolor: WA_GREEN,
                      fontWeight: 900,
                      textTransform: "none",
                      height: 44,
                      "&:hover": { bgcolor: WA_GREEN },
                    }}
                  >
                    Create Database
                  </Button>
                </span>
              </Tooltip>

              <Tooltip
                title={
                  selectedCount
                    ? "Create a database starting with selected contacts"
                    : "Select contacts to use this shortcut"
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    onClick={() => openCreateDbModal({ preselect: true })}
                    disabled={!selectionEnabled || selectedCount === 0 || loading || syncing || uploading}
                    sx={{ textTransform: "none", fontWeight: 900, height: 44 }}
                  >
                    Save Selected ({selectedCount})
                  </Button>
                </span>
              </Tooltip>

              <Chip
                label={`Selected: ${selectedCount}`}
                variant="outlined"
                sx={{ fontWeight: 900, borderColor: BORDER, bgcolor: "#F9FAFB", height: 44 }}
              />
            </Stack>
          </Stack>

          {/* straight aligned filter row */}
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(6, minmax(0, 1fr))",
              },
              gap: 1.25,
              alignItems: "start",
            }}
          >
            <TextField
              size="small"
              select
              label="Source"
              value={safeFilterModel?.source ?? ""}
              onChange={(e) => handleSourceChange(e.target.value)}
              sx={filterControlSx}
              disabled={openCreateDb}
            >
              {SOURCE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="Database"
              value={safeFilterModel?.contact_group_id ?? ""}
              onChange={(e) => handleContactGroupChange(e.target.value)}
              sx={filterControlSx}
              disabled={openCreateDb || groupsLoading}
            >
              <MenuItem value="">
                <em>All databases</em>
              </MenuItem>
              {(contactGroups || []).map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>
                  {g.name || `Group #${g.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="Lead Status"
              value={leadStatusValue || ""}
              onChange={(e) => handleLeadStatusChange(e.target.value)}
              sx={filterControlSx}
              disabled={openCreateDb}
            >
              <MenuItem value="">
                <em>All statuses</em>
              </MenuItem>
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="Contacted"
              value={safeFilterModel?.contacted ?? ""}
              onChange={(e) => handleContactedChange(e.target.value)}
              sx={filterControlSx}
              disabled={openCreateDb}
            >
              {CONTACTED_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="Contacted in last"
              value={safeFilterModel?.contacted_last_days ?? ""}
              onChange={(e) => handleContactedDaysChange(e.target.value)}
              sx={filterControlSx}
              disabled={openCreateDb || (safeFilterModel?.contacted ?? "") !== "yes"}
            >
              {CONTACTED_LAST_DAYS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Exclude Campaign"
              value={safeFilterModel?.exclude_campaign_id ?? ""}
              onChange={(e) => handleExcludeCampaignChange(e.target.value)}
              placeholder="e.g. 123"
              sx={filterControlSx}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              disabled={openCreateDb}
            />
          </Box>

          <Box sx={{ mt: 1.25 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search name, phone, email..."
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              disabled={openCreateDb}
              sx={{
                "& .MuiInputBase-root": {
                  height: 44,
                  backgroundColor: "white",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#F9FAFB", fontWeight: 900 } }}>
                {selectionEnabled && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && someVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={openCreateDb}
                    />
                  </TableCell>
                )}
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Lead Status</TableCell>
                <TableCell>Sources</TableCell>
                <TableCell align="right">Last Contacted</TableCell>
              </TableRow>
            </TableHead>

            {tableBody}
          </Table>
        </TableContainer>

        <Divider />

        <Box
          sx={{
            p: 1.5,
            bgcolor: "#FAFAFA",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Showing {rows.length} results • Total: {pageMeta.total}
            </Typography>
            {pageMeta.last_page > 1 && (
              <Pagination
                size="small"
                page={page}
                count={pageMeta.last_page}
                onChange={(_, p) => setPage(p)}
                disabled={openCreateDb}
              />
            )}
          </Stack>

          {!embedded && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 900, color: WA_GREEN, mr: 1 }}>
                {selectionEnabled ? `${selectedCount} selected` : "Preview mode"}
              </Typography>

              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={!canContinue}
                sx={{
                  bgcolor: WA_GREEN,
                  fontWeight: 900,
                  textTransform: "none",
                  "&:hover": { bgcolor: WA_GREEN },
                }}
              >
                {continueLabel} ({selectedCount})
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>

      <Dialog open={openManual} onClose={() => setOpenManual(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Add Manual Contact</DialogTitle>
        <DialogContent dividers>
          {manualErr && (
            <Alert
              severity="error"
              sx={{ mb: 2, whiteSpace: "pre-wrap" }}
              onClose={() => setManualErr("")}
            >
              {manualErr}
            </Alert>
          )}

          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              label="Name"
              value={manualForm.display_name}
              onChange={(e) => setManualForm((p) => ({ ...p, display_name: e.target.value }))}
              placeholder="e.g. John Doe"
              fullWidth
            />

            <TextField
              label="Phone (required)"
              value={manualForm.primary_phone}
              onChange={(e) => setManualForm((p) => ({ ...p, primary_phone: e.target.value }))}
              placeholder="e.g. 0771234567"
              fullWidth
              required
            />

            <TextField
              label="Email (optional)"
              value={manualForm.primary_email}
              onChange={(e) => setManualForm((p) => ({ ...p, primary_email: e.target.value }))}
              placeholder="e.g. john@example.com"
              fullWidth
            />

            <TextField
              select
              label="Category"
              value={manualForm.category}
              onChange={(e) => setManualForm((p) => ({ ...p, category: e.target.value }))}
              fullWidth
            >
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="caption" color="text.secondary">
              Note: Phone will be sanitized/normalized by the backend.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenManual(false)}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={saveManual}
            disabled={savingManual}
            startIcon={savingManual ? <CircularProgress size={16} /> : null}
            sx={{
              bgcolor: WA_GREEN,
              fontWeight: 900,
              textTransform: "none",
              "&:hover": { bgcolor: WA_GREEN },
            }}
          >
            {savingManual ? "Saving..." : "Save Contact"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateDb}
        fullScreen
        onClose={() => setOpenCreateDb(false)}
        PaperProps={{ sx: { bgcolor: BG } }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: "white", borderBottom: `1px solid ${BORDER}` }}
        >
          <Toolbar sx={{ gap: 1 }}>
            <IconButton onClick={() => setOpenCreateDb(false)} size="small">
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                Create Contact Database
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Add contacts manually or import from Excel. Save when ready.
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={createDatabase}
              disabled={creatingDb || dbContactsBusy}
              startIcon={creatingDb ? <CircularProgress size={16} /> : null}
              sx={{
                bgcolor: WA_GREEN,
                fontWeight: 900,
                textTransform: "none",
                "&:hover": { bgcolor: WA_GREEN },
              }}
            >
              {creatingDb ? "Saving..." : "Save Database"}
            </Button>

            <IconButton onClick={() => setOpenCreateDb(false)} size="small" sx={{ ml: 0.5 }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: { xs: 1.5, md: 2.5 }, display: "flex", flexDirection: "column", gap: 2 }}>
          {dbErr && (
            <Alert severity="error" sx={{ whiteSpace: "pre-wrap" }} onClose={() => setDbErr("")}>
              {dbErr}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, bgcolor: "white", p: 2 }}
          >
            <Stack spacing={1.5}>
              <TextField
                label="Database name"
                value={dbName}
                onChange={(e) => setDbName(e.target.value)}
                placeholder="e.g. Apartment Clients"
                fullWidth
                required
              />

              <TextField
                label="Description (optional)"
                value={dbDesc}
                onChange={(e) => setDbDesc(e.target.value)}
                placeholder="e.g. Clients from Apartment promotions list"
                fullWidth
                multiline
                minRows={2}
              />

              <Divider />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={startWithSelected}
                    onChange={(e) => setStartWithSelected(Boolean(e.target.checked))}
                    disabled={dbSelectedCountSnapshot === 0}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    Start with selected contacts ({dbSelectedCountSnapshot})
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{ color: "text.secondary", ml: 4.25 }}>
                Uses a snapshot of your selection.
              </Typography>
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "white",
              }}
            >
              <Box sx={{ px: 2, py: 1.25, bgcolor: "#F9FAFB", borderBottom: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontWeight: 900 }}>Add contact manually</Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                {dbManualErr && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDbManualErr("")}>
                    {dbManualErr}
                  </Alert>
                )}

                <Stack spacing={1.25}>
                  <TextField
                    label="Contact Name"
                    value={dbManual.display_name}
                    onChange={(e) => setDbManual((p) => ({ ...p, display_name: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Phone Number (required)"
                    value={dbManual.primary_phone}
                    onChange={(e) => setDbManual((p) => ({ ...p, primary_phone: e.target.value }))}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email (optional)"
                    value={dbManual.primary_email}
                    onChange={(e) => setDbManual((p) => ({ ...p, primary_email: e.target.value }))}
                    fullWidth
                  />

                  <Button
                    variant="contained"
                    onClick={addDbManualContact}
                    disabled={dbContactsBusy}
                    startIcon={dbContactsBusy ? <CircularProgress size={16} /> : null}
                    sx={{
                      bgcolor: WA_GREEN,
                      fontWeight: 900,
                      textTransform: "none",
                      "&:hover": { bgcolor: WA_GREEN },
                    }}
                  >
                    Add to Database List
                  </Button>
                </Stack>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${BORDER}`,
                borderRadius: 3,
                overflow: "hidden",
                bgcolor: "white",
              }}
            >
              <Box sx={{ px: 2, py: 1.25, bgcolor: "#F9FAFB", borderBottom: `1px solid ${BORDER}` }}>
                <Typography sx={{ fontWeight: 900 }}>Import from Excel</Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                  WhatsApp databases require <b>phone numbers</b>. Email-only rows will be ignored.
                </Typography>

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={dbContactsBusy ? <CircularProgress size={16} /> : <UploadIcon />}
                  disabled={dbContactsBusy}
                  sx={{ textTransform: "none", fontWeight: 900 }}
                >
                  Import Excel/CSV
                  <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleDbImportFile} />
                </Button>
              </Box>
            </Paper>
          </Stack>

          <Paper
            elevation={0}
            sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, overflow: "hidden", bgcolor: "white" }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: "#F9FAFB",
                borderBottom: `1px solid ${BORDER}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontWeight: 900 }}>Database contacts</Typography>
              <Chip
                label={`In list: ${dbContacts.length}`}
                variant="outlined"
                sx={{ fontWeight: 900, borderColor: BORDER }}
              />
            </Box>

            <Box sx={{ p: 1.5 }}>
              {dbContactsBusy ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <CircularProgress />
                </Box>
              ) : dbContacts.length === 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No contacts added yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 900, bgcolor: "#FAFAFA" } }}>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{dbContactsRows}</TableBody>
                </Table>
              )}
            </Box>
          </Paper>

          <Dialog open={Boolean(dbEditing)} onClose={() => setDbEditing(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 900 }}>Edit Contact</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.25}>
                <TextField
                  label="Contact Name"
                  value={dbEditing?.contact?.display_name ?? ""}
                  onChange={(e) =>
                    setDbEditing((p) =>
                      p ? { ...p, contact: { ...p.contact, display_name: e.target.value } } : p
                    )
                  }
                  fullWidth
                />
                <TextField
                  label="Phone Number (required)"
                  value={dbEditing?.contact?.primary_phone ?? ""}
                  onChange={(e) =>
                    setDbEditing((p) =>
                      p ? { ...p, contact: { ...p.contact, primary_phone: e.target.value } } : p
                    )
                  }
                  fullWidth
                  required
                />
                <TextField
                  label="Email (optional)"
                  value={dbEditing?.contact?.primary_email ?? ""}
                  onChange={(e) =>
                    setDbEditing((p) =>
                      p ? { ...p, contact: { ...p.contact, primary_email: e.target.value } } : p
                    )
                  }
                  fullWidth
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setDbEditing(null)}
                sx={{ fontWeight: 900, textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveEditDbContact}
                disabled={dbContactsBusy}
                sx={{
                  bgcolor: WA_GREEN,
                  fontWeight: 900,
                  textTransform: "none",
                  "&:hover": { bgcolor: WA_GREEN },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Dialog>
    </Box>
  );

  if (embedded) return content;

  return (
    <Dialog open={open} fullScreen={fullScreen} onClose={onClose}>
      {content}
    </Dialog>
  );
}
